import React, { useState, useEffect } from 'react';
import { Settings, DollarSign, TrendingUp, Edit2, Save, X, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    container: {
      position: 'fixed',
      top: '2rem',
      right: '2rem',
      background: type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      padding: '1rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      zIndex: 10000,
      border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
      minWidth: '300px',
      color: 'white',
    },
  };

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      style={styles.container}
    >
      <span>{message}</span>
    </motion.div>
  );
};

const FeaturesManagement = () => {
  const [features, setFeatures] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load features
      const featuresResponse = await fetch(`${API_URL}/admin/features`, {
        credentials: 'include',
      });
      const featuresData = await featuresResponse.json();
      setFeatures(featuresData.features);

      // Load revenue stats
      const revenueResponse = await fetch(`${API_URL}/admin/revenue-stats`, {
        credentials: 'include',
      });
      const revenueData = await revenueResponse.json();
      setRevenueStats(revenueData);
    } catch (error) {
      console.error('Failed to load data:', error);
      showToastMessage('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature.id);
    setEditForm({ ...feature });
  };

  const handleSaveFeature = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/features/${editingFeature}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        await loadData();
        setEditingFeature(null);
        setEditForm({});
        showToastMessage('Feature updated successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save feature:', error);
      showToastMessage('Failed to save feature', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingFeature(null);
    setEditForm({});
  };

  const handleToggleFeature = async (feature) => {
    try {
      const response = await fetch(`${API_URL}/admin/features/${feature.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...feature,
          is_enabled: !feature.is_enabled
        }),
      });

      if (response.ok) {
        await loadData();
        showToastMessage(`Feature ${!feature.is_enabled ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Failed to toggle feature:', error);
      showToastMessage('Failed to toggle feature', 'error');
    }
  };

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    statCard: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      padding: '1.5rem',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
      position: 'relative',
      overflow: 'hidden',
    },
    statGlow: {
      position: 'absolute',
      top: '-50%',
      right: '-10%',
      width: '150px',
      height: '150px',
      background: 'radial-gradient(circle, rgba(0, 255, 245, 0.15) 0%, transparent 70%)',
      borderRadius: '50%',
    },
    statLabel: {
      fontSize: '0.9rem',
      color: '#a0a0c0',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontFamily: "'Rajdhani', sans-serif",
      marginBottom: '0.5rem',
      position: 'relative',
      zIndex: 1,
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 900,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      position: 'relative',
      zIndex: 1,
    },
    section: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
      padding: '2rem',
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      color: '#00fff5',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    featureCard: {
      background: 'rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      borderRadius: '12px',
      padding: '1.5rem',
      marginBottom: '1rem',
      transition: 'all 0.3s ease',
    },
    featureCardHover: {
      borderColor: '#00fff5',
      boxShadow: '0 4px 20px rgba(0, 255, 245, 0.2)',
    },
    featureHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    featureName: {
      fontSize: '1.25rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      color: '#00fff5',
      marginBottom: '0.25rem',
    },
    featureDescription: {
      fontSize: '0.9rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      lineHeight: 1.5,
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginTop: '1rem',
    },
    featureDetail: {
      background: 'rgba(0, 255, 245, 0.05)',
      borderRadius: '8px',
      padding: '0.75rem',
      border: '1px solid rgba(0, 255, 245, 0.1)',
    },
    detailLabel: {
      fontSize: '0.75rem',
      color: '#8b5cf6',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontFamily: "'Rajdhani', sans-serif",
      marginBottom: '0.25rem',
    },
    detailValue: {
      fontSize: '1.1rem',
      color: '#00fff5',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
    },
    input: {
      background: 'rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '8px',
      padding: '0.5rem',
      color: '#e0e0ff',
      fontFamily: "'Rajdhani', sans-serif",
      fontSize: '0.95rem',
      outline: 'none',
      width: '100%',
    },
    textarea: {
      background: 'rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '8px',
      padding: '0.75rem',
      color: '#e0e0ff',
      fontFamily: "'Rajdhani', sans-serif",
      fontSize: '0.95rem',
      outline: 'none',
      width: '100%',
      minHeight: '80px',
      resize: 'vertical',
    },
    checkbox: {
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      accentColor: '#00fff5',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#e0e0ff',
      fontFamily: "'Rajdhani', sans-serif",
      fontSize: '0.95rem',
      cursor: 'pointer',
    },
    button: {
      padding: '0.75rem 1.5rem',
      background: 'rgba(0, 255, 245, 0.1)',
      border: '1px solid #00fff5',
      borderRadius: '8px',
      color: '#00fff5',
      cursor: 'pointer',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 600,
      fontSize: '0.95rem',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    saveButton: {
      background: 'rgba(16, 185, 129, 0.2)',
      borderColor: '#10b981',
      color: '#10b981',
    },
    cancelButton: {
      background: 'rgba(239, 68, 68, 0.2)',
      borderColor: '#ef4444',
      color: '#ef4444',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    enabledBadge: {
      background: 'rgba(16, 185, 129, 0.2)',
      color: '#10b981',
      border: '1px solid #10b981',
    },
    disabledBadge: {
      background: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
      border: '1px solid #ef4444',
    },
    premiumBadge: {
      background: 'rgba(139, 92, 246, 0.2)',
      color: '#8b5cf6',
      border: '1px solid #8b5cf6',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
    },
    toggleButton: {
      padding: '0.5rem',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#a0a0c0',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    revenueTable: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      padding: '1rem',
      borderBottom: '2px solid rgba(0, 255, 245, 0.2)',
      color: '#00fff5',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.9rem',
      letterSpacing: '1px',
    },
    td: {
      padding: '1rem',
      borderBottom: '1px solid rgba(0, 255, 245, 0.1)',
      color: '#e0e0ff',
      fontFamily: "'Rajdhani', sans-serif",
    },
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, textAlign: 'center', padding: '4rem' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Settings size={48} color="#00fff5" />
        </motion.div>
        <div style={{ color: '#a0a0c0', marginTop: '1rem', fontFamily: "'Rajdhani', sans-serif" }}>
          Loading features...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {showToast && (
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>

      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          FEATURES & MONETIZATION
        </motion.h1>
        <p style={styles.subtitle}>Configure feature access, pricing, and monitor revenue</p>
      </div>

      {/* Revenue Stats */}
      {revenueStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.statsGrid}
        >
          <div style={styles.statCard}>
            <div style={styles.statGlow} />
            <div style={styles.statLabel}>Total Revenue</div>
            <div style={styles.statValue}>${revenueStats.total_revenue.toFixed(2)}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statGlow} />
            <div style={styles.statLabel}>Monthly Recurring</div>
            <div style={styles.statValue}>${revenueStats.mrr.toFixed(2)}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statGlow} />
            <div style={styles.statLabel}>Premium Users</div>
            <div style={styles.statValue}>{revenueStats.active_premium_users}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statGlow} />
            <div style={styles.statLabel}>One-Time Revenue</div>
            <div style={styles.statValue}>${revenueStats.one_time_revenue.toFixed(2)}</div>
          </div>
        </motion.div>
      )}

      {/* Feature Configuration */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          <Settings size={24} />
          Feature Configuration
        </div>

        {features.map((feature, index) => (
          <motion.div
            key={feature.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            style={styles.featureCard}
            whileHover={styles.featureCardHover}
          >
            {editingFeature === feature.id ? (
              // Edit Mode
              <div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ ...styles.detailLabel, display: 'block', marginBottom: '0.5rem' }}>
                    Feature Name (System)
                  </label>
                  <input
                    type="text"
                    value={editForm.feature_name}
                    disabled
                    style={{ ...styles.input, opacity: 0.5 }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ ...styles.detailLabel, display: 'block', marginBottom: '0.5rem' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                    style={styles.input}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ ...styles.detailLabel, display: 'block', marginBottom: '0.5rem' }}>
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    style={styles.textarea}
                  />
                </div>

                <div style={styles.featureGrid}>
                  <div>
                    <label style={{ ...styles.detailLabel, display: 'block', marginBottom: '0.5rem' }}>
                      One-Time Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.one_time_price}
                      onChange={(e) => setEditForm({ ...editForm, one_time_price: parseFloat(e.target.value) })}
                      style={styles.input}
                    />
                  </div>

                  <div>
                    <label style={{ ...styles.detailLabel, display: 'block', marginBottom: '0.5rem' }}>
                      Credits per Purchase
                    </label>
                    <input
                      type="number"
                      value={editForm.credits_per_purchase}
                      onChange={(e) => setEditForm({ ...editForm, credits_per_purchase: parseInt(e.target.value) })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editForm.requires_premium}
                      onChange={(e) => setEditForm({ ...editForm, requires_premium: e.target.checked })}
                      style={styles.checkbox}
                    />
                    Requires Premium Subscription
                  </label>

                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editForm.allow_one_time_purchase}
                      onChange={(e) => setEditForm({ ...editForm, allow_one_time_purchase: e.target.checked })}
                      style={styles.checkbox}
                    />
                    Allow One-Time Purchase
                  </label>

                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editForm.is_enabled}
                      onChange={(e) => setEditForm({ ...editForm, is_enabled: e.target.checked })}
                      style={styles.checkbox}
                    />
                    Feature Enabled
                  </label>
                </div>

                <div style={{ ...styles.buttonGroup, marginTop: '1.5rem' }}>
                  <motion.button
                    onClick={handleSaveFeature}
                    style={{ ...styles.button, ...styles.saveButton }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Save size={16} />
                    Save Changes
                  </motion.button>
                  <motion.button
                    onClick={handleCancelEdit}
                    style={{ ...styles.button, ...styles.cancelButton }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <X size={16} />
                    Cancel
                  </motion.button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div style={styles.featureHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.featureName}>{feature.display_name}</div>
                    <div style={styles.featureDescription}>{feature.description}</div>
                    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{
                        ...styles.badge,
                        ...(feature.is_enabled ? styles.enabledBadge : styles.disabledBadge),
                      }}>
                        {feature.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      {feature.requires_premium && (
                        <span style={{ ...styles.badge, ...styles.premiumBadge }}>
                          Premium Only
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={styles.buttonGroup}>
                    <motion.button
                      onClick={() => handleToggleFeature(feature)}
                      style={{
                        ...styles.toggleButton,
                        color: feature.is_enabled ? '#10b981' : '#ef4444',
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title={feature.is_enabled ? 'Disable feature' : 'Enable feature'}
                    >
                      {feature.is_enabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                    </motion.button>
                    <motion.button
                      onClick={() => handleEditFeature(feature)}
                      style={styles.button}
                      whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Edit2 size={16} />
                      Edit
                    </motion.button>
                  </div>
                </div>

                <div style={styles.featureGrid}>
                  <div style={styles.featureDetail}>
                    <div style={styles.detailLabel}>System Name</div>
                    <div style={styles.detailValue}>{feature.feature_name}</div>
                  </div>
                  <div style={styles.featureDetail}>
                    <div style={styles.detailLabel}>One-Time Price</div>
                    <div style={styles.detailValue}>${feature.one_time_price}</div>
                  </div>
                  <div style={styles.featureDetail}>
                    <div style={styles.detailLabel}>Credits/Purchase</div>
                    <div style={styles.detailValue}>{feature.credits_per_purchase}</div>
                  </div>
                  <div style={styles.featureDetail}>
                    <div style={styles.detailLabel}>One-Time Purchase</div>
                    <div style={styles.detailValue}>{feature.allow_one_time_purchase ? 'YES' : 'NO'}</div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Feature Revenue Breakdown */}
      {revenueStats && revenueStats.feature_revenue.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <TrendingUp size={24} />
            Revenue by Feature
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={styles.revenueTable}>
              <thead>
                <tr>
                  <th style={styles.th}>Feature</th>
                  <th style={styles.th}>Total Revenue</th>
                  <th style={styles.th}>Purchases</th>
                  <th style={styles.th}>Avg per Purchase</th>
                  <th style={styles.th}>Performance</th>
                </tr>
              </thead>
              <tbody>
                {revenueStats.feature_revenue.map((fr, idx) => (
                  <motion.tr
                    key={idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <td style={styles.td}>{fr.feature}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.detailValue, fontSize: '1rem' }}>
                        ${fr.revenue.toFixed(2)}
                      </span>
                    </td>
                    <td style={styles.td}>{fr.purchases}</td>
                    <td style={styles.td}>${(fr.revenue / fr.purchases).toFixed(2)}</td>
                    <td style={styles.td}>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        background: 'rgba(0, 255, 245, 0.1)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${Math.min((fr.revenue / revenueStats.one_time_revenue) * 100, 100)}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #00fff5, #8b5cf6)',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturesManagement;